import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Very small hash-based nav so we don't introduce BrowserRouter now.
 * Route is stored in window.location.hash after `#/`.
 * Examples: "/" (home), "/civil", "/criminal", "/criminal/bail-affidavit",
 * "/criminal/bail-affidavit/:caseId"
 */
const NavCtx = createContext(null);

function readHashRoute() {
  const h = (typeof window !== "undefined" ? window.location.hash : "") || "";
  if (!h) return "/";
  const m = h.replace(/^#/, "");
  if (!m.startsWith("/")) return "/";
  return m || "/";
}

export function NavProvider({ children }) {
  const [route, setRoute] = useState(readHashRoute());

  useEffect(() => {
    const onChange = () => setRoute(readHashRoute());
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);

  const navigate = useCallback((path) => {
    const p = path.startsWith("/") ? path : `/${path}`;
    if (window.location.hash !== `#${p}`) {
      window.location.hash = `#${p}`;
    }
    setRoute(p);
  }, []);

  return (
    <NavCtx.Provider value={{ route, navigate }}>{children}</NavCtx.Provider>
  );
}

export function useNav() {
  const v = useContext(NavCtx);
  if (!v) throw new Error("useNav must be used within NavProvider");
  return v;
}

/** Match `/foo/:bar` style routes; returns null or { params } */
export function matchRoute(route, pattern) {
  const routeParts = route.split("/").filter(Boolean);
  const patternParts = pattern.split("/").filter(Boolean);
  if (routeParts.length !== patternParts.length) return null;
  const params = {};
  for (let i = 0; i < patternParts.length; i++) {
    const pp = patternParts[i];
    if (pp.startsWith(":")) params[pp.slice(1)] = decodeURIComponent(routeParts[i]);
    else if (pp !== routeParts[i]) return null;
  }
  return { params };
}
